<template>
    <QualificationField :title="$t('profile.vetting.community.vertical.title')" data-testid="admin-section-tabs-qualification-vertical">
        <ul class="states">
            <li v-if="!isStrategyPromotionAvailable">
                <SuggestionWarning :warnings="reasonsStrategyNotAvailable" />
            </li>
            <li
                v-for="verticalStatus in verticalState"
                :key="verticalStatus.vertical"
                :data-testid="`vetting-vertical-state__${verticalStatus.vertical}`"
            >
                <VJoyBadge
                    :variant="applicationStatusColor[verticalStatus.status]"
                    :label="verticalStatus.status"
                    bulletpoint
                    data-testid="vetting-vertical-state-status"
                />
                <span class="vertical" data-testid="vetting-vertical-state-vertical">{{ verticalStatus.vertical }}</span>
                <span v-if="verticalStatus.showVerticalRefuseSilentlyToStrategy" class="action">
                    <VJoyButton
                        class="redirection__button"
                        variant="ghost"
                        size="xsmall"
                        :label="$t('profile.vetting.community.vetting.decision.cancel.suggestion')"
                        data-testid="vetting-vertical-cancel-strategy"
                        @click="handleRefuseSilentlyToStrategy"
                    />
                </span>
                <span v-if="verticalStatus.showVerticalAcceptToRegular" class="action">
                    <VJoyButton
                        class="redirection__button"
                        variant="ghost"
                        size="xsmall"
                        :label="$t('profile.vetting.community.vetting.decision.redirect.to.marketplace')"
                        data-testid="vetting-vertical-accept-regular"
                        @click="handleAcceptToRegular"
                    />
                </span>
                <span v-if="verticalStatus.showVerticalSuggestToStrategy" class="action">
                    <VJoyButton
                        class="redirection__button"
                        variant="ghost"
                        size="xsmall"
                        :label="$t('profile.vetting.community.vetting.decision.suggest.to.strategy')"
                        data-testid="vetting-vertical-apply-strategy"
                        @click="handleSuggestToStrategy"
                    />
                </span>
            </li>
        </ul>
    </QualificationField>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {VJoyBadge, VJoyButton} from '@maltjoy/core-vue';
    import type {FreelancerVerticalApplicationStatus, FreelancerVerticalCompatibilityWithStrategyInner, FreelancerVerticalStateInner} from '@api';
    import {FreelancerVertical} from '@api';
    import SuggestionWarning from '~/components/admin-section-tabs/community/SuggestionWarning.vue';
    import {eventBus} from '~/config/events';
    import {useFreelancerVerticalStore} from '~/store/freelancerVerticalStore';
    import {useProfileQualificationStore} from '~/store/profileQualificationStore';
    import QualificationField from './ui/QualificationField.vue';

    const freelancerVerticalStore = useFreelancerVerticalStore();
    const {freelancerVerticalState, compatibilityToStrategyPromotion} = storeToRefs(freelancerVerticalStore);
    const profileQualificationStore = useProfileQualificationStore();
    const {inEditMode} = storeToRefs(profileQualificationStore);

    const vertical: Record<FreelancerVertical, string> = {
        REGULAR: 'Marketplace',
        STRATEGY: 'Strategy',
    };

    const applicationStatus: Record<FreelancerVerticalApplicationStatus, string> = {
        PENDING: 'pending',
        ACCEPTED: 'accepted',
        REFUSED: 'denied',
        REMOVED: 'former',
    };

    const applicationStatusColor: Record<string, any> = {
        pending: 'orange',
        accepted: 'green',
        denied: 'red',
        former: 'gray',
    };

    const isStrategyPromotionAvailable = computed(
        () =>
            (compatibilityToStrategyPromotion.value as FreelancerVerticalCompatibilityWithStrategyInner[]).filter(
                (compatibility) => !compatibility.compatible,
            ).length === 0,
    );
    const reasonsStrategyNotAvailable = computed(() =>
        (compatibilityToStrategyPromotion.value as FreelancerVerticalCompatibilityWithStrategyInner[])
            .filter((compatibility) => !compatibility.compatible)
            .map((compatibility) => {
                switch (compatibility.profileInfo) {
                    case 'CATEGORY':
                        return 'Current job category is not eligible to Malt Strategy';
                    default:
                        return '';
                }
            }),
    );

    const verticalState = computed(() => {
        return (freelancerVerticalState.value as FreelancerVerticalStateInner[]).map((state) => {
            const isEditionAllowed = freelancerVerticalStore.verticalChangesEnabled && !inEditMode.value;
            return {
                vertical: vertical[state.freelancerVertical],
                status: applicationStatus[state.freelancerVerticalApplicationStatus],
                showVerticalRefuseSilentlyToStrategy:
                    state.freelancerVertical === FreelancerVertical.Strategy &&
                    freelancerVerticalStore.isStrategyPending &&
                    freelancerVerticalStore.isRegularAccepted &&
                    isEditionAllowed,
                showVerticalAcceptToRegular:
                    state.freelancerVertical === FreelancerVertical.Strategy && freelancerVerticalStore.isStrategyAccepted && isEditionAllowed,
                showVerticalSuggestToStrategy:
                    state.freelancerVertical === FreelancerVertical.Regular &&
                    !freelancerVerticalStore.isStrategyPending &&
                    freelancerVerticalStore.isRegularAccepted &&
                    isEditionAllowed,
                isStrategyPromotionAvailable: isStrategyPromotionAvailable.value,
            };
        });
    });

    function handleRefuseSilentlyToStrategy() {
        eventBus.emit('verticalRefuseSilentlyToStrategy:open');
    }

    function handleAcceptToRegular() {
        eventBus.emit('verticalAcceptToRegular:open');
    }

    function handleSuggestToStrategy() {
        eventBus.emit('verticalSuggestToStrategy:open', reasonsStrategyNotAvailable.value);
    }
</script>

<style lang="scss" scoped>
    .states {
        list-style-type: none;
        font-size: var(--joy-font-size-primary-300);
        padding: 0;
        margin: 0;

        li {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            padding: 5px 0;

            .vertical {
                margin-left: 10px;
                font-weight: var(--joy-font-weight-bold);
            }

            .no-strategy-icon {
                margin-right: 5px;
            }
        }
    }
</style>
